import { Skeleton } from '@dixa/lib-shared-habla';
import { formatInstrumentNumber, MetricInstrumentData } from 'lib-analytics-ui';
import { QueuedTimeMetric } from 'lib-dashboard-types';
import { ampli } from 'lib-shared-tracking';
import { PropsWithClassName } from 'lib-shared-types-utils';
import moment from 'moment-timezone';
import { memo, useMemo } from 'react';

import {
  QueueDashboardMetric,
  type QueueDashboardMetricProps,
} from 'app/views/layout/views/dashboard/views/queue/components/queues/components/metric';
import { AppLink } from 'components/appLink';

import { useQueueDashboardQueueContext } from '../../../queue/queueDashboardQueue.context';

type QueueDashboardQueuedTimeMetricProps = {
  metric?: QueuedTimeMetric;
} & PropsWithClassName;

export const QueueDashboardQueuedTimeMetric = memo(
  function QueueDashboardQueuedTimeMetric({
    className,
    metric,
  }: QueueDashboardQueuedTimeMetricProps): JSX.Element {
    const { queue, isSkeleton } = useQueueDashboardQueueContext();

    const thresholdAlertTooltip =
      useMemo((): QueueDashboardMetricProps['alertTooltip'] => {
        if (
          !metric?.threshold_breached ||
          !queue?.queue_thresholds?.longest_wait
        ) {
          return null;
        }

        return {
          head: 'Current longest wait in a queue alert',
          content: (
            <>
              This queue has passed the maximum time a conversation is allowed
              to wait in a queue, which is{' '}
              <b>
                {formatInstrumentNumber(
                  queue.queue_thresholds.longest_wait * 1000,
                  'timer',
                )}
              </b>
              .
            </>
          ),
        };
      }, [metric, queue]);

    /**
     * The displayed metric data is different based on whether the longest
     * queued time is longer than 1 day or not. If longer, we display a static
     * short duration, and if shorter we display a full live timer.
     */
    const metricData = useMemo((): JSX.Element => {
      const milliseconds = moment().diff(
        moment(metric?.longest_queued_time),
        'milliseconds',
      );
      const isOverOneDay = milliseconds > 24 * 60 * 60 * 1000;

      const data = isOverOneDay ? (
        <MetricInstrumentData
          data={milliseconds}
          format="duration"
          suffix="+"
        />
      ) : (
        <MetricInstrumentData
          data={metric?.longest_queued_time ?? null}
          format="timer"
        />
      );

      const longestQueuedCsid = metric?.longest_queued_conversations?.[0]?.csid;

      if (longestQueuedCsid) {
        return (
          <AppLink
            path="/conversation/:csid"
            params={{ path: { csid: `${longestQueuedCsid}` } }}
            onClick={() => {
              ampli.dashboardLongestQueuedTimeMetricClicked({
                source: queue ? 'queue' : 'global',
              });
            }}
          >
            {data}
          </AppLink>
        );
      }
      return data;
    }, [metric, queue]);

    return (
      <QueueDashboardMetric
        className={className}
        metricType="queued_time"
        isSkeleton={isSkeleton}
        alertTooltip={thresholdAlertTooltip}
      >
        {isSkeleton ? <Skeleton height="24px" width="72px" /> : metricData}
      </QueueDashboardMetric>
    );
  },
);
